import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AnimatedSection from '../components/AnimatedSection';
import StaggeredList from '../components/StaggeredList';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Phone,
  Mail,
  Globe,
  CheckCircle,
  Users,
  Truck,
  Handshake,
  MapPin
} from 'lucide-react';

const FindDistributorPage = () => {

  const distributor = {
    name: "MedOne Group",
    type: "national",
    localStates: ["CA", "FL", "GA", "UT", "AZ", "TX", "NV", "CO", "NC", "SC", "VA"],
    otherCoverage: ["All US"],
    phone: "(800) 248-5882",
    email: "info@medonegroup.com",
    website: "www.medonegroup.com",
    specialties: ["Critical Care", "Nursing Supplies", "Hospital Rentals"],
    description: "Leading national distributor specializing in critical care equipment and hospital rentals with comprehensive service and support"
  };

  const distributorServices = [
    {
      title: "Product Distribution",
      description: "Nationwide distribution network ensuring timely delivery",
      icon: <Truck className="h-6 w-6" />,
      color: "#214140"
    },
    {
      title: "Clinical Training",
      description: "On-site training and education programs for healthcare staff",
      icon: <Users className="h-6 w-6" />,
      color: "#8BBAB8"
    },
    {
      title: "Technical Support",
      description: "Technical support and troubleshooting assistance",
      icon: <Phone className="h-6 w-6" />,
      color: "#1F8051"
    },
    {
      title: "Implementation Support",
      description: "Complete implementation support from setup to go-live",
      icon: <CheckCircle className="h-6 w-6" />,
      color: "#214140"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-24">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-b from-white to-gray-50">
          <div className="container mx-auto px-4">
            <AnimatedSection animation="fade-up">
              <div className="text-center max-w-4xl mx-auto">
                <h1 className="text-4xl lg:text-5xl font-bold mb-6" style={{ color: '#214140' }}>
                  Find a Distributor
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed mb-8">
                  Connect with our authorized Cordflex® distributor for product access,
                  local support, training, and implementation services.
                </p>
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* Distributor */}
        <section className="py-20" style={{ backgroundColor: '#DFEAF0' }}>
          <div className="container mx-auto px-4">
            <AnimatedSection animation="fade-up">
              <div className="text-center mb-16">
                <h2 className="text-3xl lg:text-4xl font-bold mb-4" style={{ color: '#214140' }}>
                  Distributors
                </h2>
                <p className="text-xl text-gray-600">
                  Our authorized distribution partners
                </p>
              </div>
            </AnimatedSection>

            <Card className="max-w-6xl mx-auto bg-white shadow-xl">
              <CardContent className="p-8">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <div className="flex items-center space-x-3 mb-2">
                      <Badge style={{ backgroundColor: '#8BBAB8', color: 'white' }}>
                        Authorized Partner
                      </Badge>
                      <Badge variant="secondary" style={{ backgroundColor: '#DFEAF0', color: '#214140' }}>
                        National
                      </Badge>
                    </div>
                    <h3 className="text-2xl font-bold mb-2" style={{ color: '#214140' }}>
                      {distributor.name}
                    </h3>
                    <p className="text-gray-600 mb-4">{distributor.description}</p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                  <div>
                    <h4 className="font-semibold mb-2" style={{ color: '#214140' }}>Local Coverage Areas:</h4>
                    <div className="flex flex-wrap gap-1">
                      {distributor.localStates.map((state, index) => (
                        <span key={index} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                          {state}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2" style={{ color: '#214140' }}>Other Coverage:</h4>
                    <div className="flex flex-wrap gap-1">
                      {distributor.otherCoverage.map((area, index) => (
                        <span key={index} className="text-xs px-2 py-1 rounded" style={{ backgroundColor: '#DFEAF0', color: '#214140' }}>
                          {area}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2" style={{ color: '#214140' }}>Specialties:</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {distributor.specialties.map((specialty, index) => (
                        <li key={index} className="flex items-center space-x-2">
                          <div className="w-1 h-1 bg-teal-500 rounded-full"></div>
                          <span>{specialty}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2" style={{ color: '#214140' }}>Contact Info:</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center space-x-2">
                        <Phone className="h-4 w-4 text-gray-400" />
                        <span>{distributor.phone}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Mail className="h-4 w-4 text-gray-400" />
                        <span>{distributor.email}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Globe className="h-4 w-4 text-gray-400" />
                        <a href={`https://${distributor.website}`} target="_blank" rel="noopener noreferrer" className="text-teal-700 hover:underline">{distributor.website}</a>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t border-gray-200">
                  <Button
                    onClick={() => window.location.href = `tel:${distributor.phone.replace(/[^0-9]/g, '')}`}
                    className="flex-1 hover:opacity-90 transition-opacity duration-200"
                    style={{ backgroundColor: '#214140', color: 'white' }}
                  >
                    <Phone className="h-4 w-4 mr-2" />
                    Call Distributor
                  </Button>
                  <Button
                    onClick={() => window.location.href = `mailto:${distributor.email}?subject=Cordflex Product Inquiry`}
                    className="flex-1 hover:opacity-90 transition-opacity duration-200"
                    style={{ backgroundColor: '#214140', color: 'white' }}
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Email Distributor
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => window.open(`https://${distributor.website}`, '_blank')}
                  >
                    <Globe className="h-4 w-4 mr-2" />
                    Visit Website
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Distributor Services */}
        <section className="py-20" style={{ backgroundColor: '#273139' }}>
          <div className="container mx-auto px-4">
            <AnimatedSection animation="fade-up">
              <div className="text-center mb-16">
                <h2 className="text-3xl lg:text-4xl font-bold mb-4 text-white">
                  What Our Distributors Provide
                </h2>
                <p className="text-xl text-gray-300">
                  Comprehensive support beyond just product distribution
                </p>
              </div>
            </AnimatedSection>

            <StaggeredList delay={150} className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {distributorServices.map((service, index) => (
                <Card key={index} className="bg-white shadow-lg text-center">
                  <CardContent className="p-6">
                    <div
                      className="w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto"
                      style={{ backgroundColor: service.color, color: 'white' }}
                    >
                      {service.icon}
                    </div>
                    <h3 className="text-lg font-semibold mb-2" style={{ color: '#214140' }}>
                      {service.title}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {service.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </StaggeredList>
          </div>
        </section>

        {/* Become a Distributor */}
        <section id="become-distributor" className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <AnimatedSection animation="fade-up">
              <div className="text-center max-w-3xl mx-auto mb-12">
                <div className="w-20 h-20 rounded-full flex items-center justify-center mb-6 mx-auto" style={{ backgroundColor: '#8BBAB8', color: 'white' }}>
                  <Handshake className="h-10 w-10" />
                </div>
                <h2 className="text-3xl lg:text-4xl font-bold mb-6" style={{ color: '#214140' }}>
                  Become a Distributor
                </h2>
                <p className="text-xl text-gray-600 leading-relaxed mb-8">
                  Interested in distributing Cordflex? We're looking for qualified partners to help bring
                  our innovative cord management solutions to healthcare facilities. Join our network and
                  benefit from exclusive territory rights, competitive margins, and comprehensive support.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    className="px-8 py-4 text-lg font-semibold hover:opacity-90 transition-opacity duration-200"
                    style={{ backgroundColor: '#214140', color: 'white' }}
                    onClick={() => window.location.href = '/contact'}
                  >
                    <Mail className="h-5 w-5 mr-2" />
                    Contact Us
                  </Button>
                  <Button
                    variant="outline"
                    className="px-8 py-4 text-lg font-semibold"
                    onClick={() => window.location.href = '/partners#partnership-opportunities'}
                  >
                    <Handshake className="h-5 w-5 mr-2" />
                    Learn About Partnering
                  </Button>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* Seeking Additional Partners */}
        <section className="py-16" style={{ backgroundColor: '#DFEAF0' }}>
          <div className="container mx-auto px-4">
            <AnimatedSection animation="fade-up">
              <div className="text-center max-w-3xl mx-auto">
                <MapPin className="h-10 w-10 mx-auto mb-4" style={{ color: '#214140' }} />
                <h2 className="text-2xl lg:text-3xl font-bold mb-4" style={{ color: '#214140' }}>
                  Expanding Our Distribution Network
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  We are currently seeking additional distributor partners in the <strong>United States</strong>, <strong>Canada</strong>, and <strong>Europe</strong>.
                  If you're an established medical device distributor in these regions, we'd love to hear from you.
                </p>
                <Button
                  className="px-8 py-4 text-lg font-semibold hover:opacity-90 transition-opacity duration-200"
                  style={{ backgroundColor: '#214140', color: 'white' }}
                  onClick={() => window.location.href = '/contact'}
                >
                  <Globe className="h-5 w-5 mr-2" />
                  Inquire About Distribution
                </Button>
              </div>
            </AnimatedSection>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default FindDistributorPage;
